import { FormText } from '../FormFields/FormText'
import { useAppSelector } from '../../store/hooks'
import { FormSelect } from '../FormFields/FormSelect'
import {
  DepartmentInterfase,
  ProfileInterfase,
  TaxIdentificationTypeInterface,
  UserAccountsInterfase,
} from '../../interfaces'
import { FormikErrors, FormikProps, FormikTouched } from 'formik'
import { useCallback, useEffect, useState } from 'react'
import { GeneralTypePhoneServices } from '../../services/GeneralServices'
import { GeneralTypeInterface } from '../../interfaces/Dto/GeneralTypeInterface'
import { ConfirmModalStatus } from '../Preshipment/ModalSuccess'
import { ModalLoading } from '../Atoms/ModalLoading'
import { FormSearch } from '../FormFields/FormSearch'
import { filterGeneral, filterProfile } from '../../utils/filters'
import { GenericoOption, ProfileOption } from '../FormFields/FormSearchOptions'
import {
  getAllDepartament,
  getAllProfile,
  getAllUser,
  getUserProfileInApp,
} from '../../services/UserServices'
import { UsersSearchHistoric } from './UsersSearch'
import { obtenerCodigoArea, obtenerNumero } from '../../utils/format'
import {
  buildUserLogin,
  LOGIN_TAKEN_MESSAGE,
  phoneDigitsFor,
  pickProfileToPreload,
  SUPER_ADMIN_PROFILE_ID,
  USER_FIELD_LIMITS,
} from '../../utils/userValidation'
import {
  ArrowPathIcon,
  CheckIcon,
  LockClosedIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

export interface UserFormValues {
  userID?: string
  accountID?: string
  isValidatonLogin?: number
  userLogin: string
  userName: string
  userLastName: string
  userIdentificationTypeID?: TaxIdentificationTypeInterface
  isValidatonIdenti?: number
  userIdentificationNumber: string
  profileID?: number
  departamentID?: number
  statusID?: number
  userPhone: string
  isValidatonEmail?: number
  userEmail: string
  codePhoneNumber?: string
  accountSelected?: UserAccountsInterfase
  reset?: boolean
  id?: string
  accountSiteID?: number
}

interface UserFormField {
  user: UserFormValues
}
interface UserFormProps<T> {
  formik: FormikProps<T & UserFormField>
  loadingUser?: number
}

export const UserForm = <T extends any>({
  formik,
  loadingUser,
}: UserFormProps<T>) => {
  const identificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes,
  )
  const user = useAppSelector((state) => state.user)!
  const errors = formik.errors.user as FormikErrors<UserFormValues> | undefined
  const touched = formik.touched.user as
    | FormikTouched<UserFormValues>
    | undefined

  const [phonePreSearch, setPhonePreSearch] = useState('')
  const [selectUserAccount, setselectUserAccount] = useState<boolean>(false)
  const [phonePrefixList, setPhonePrefixList] = useState<string[]>([])

  const [disabledIdentification, setDisabledIdentification] =
    useState<boolean>(false)
  let [loading, setLoading] = useState(false)
  let [loadingStatus, setLoadingStatus] = useState(
    ConfirmModalStatus.PROCESSING,
  )
  let [loadingTitle, setLoadingTitle] = useState('Cargando...')

  const updatePhoneSearch = useCallback(() => {
    if (!!formik.values.user.codePhoneNumber) {
      setPhonePreSearch(formik.values.user.codePhoneNumber)
    } else {
      setPhonePreSearch('')
    }
  }, [formik.values.user.codePhoneNumber])

  // El combo de Operadora sólo se sincronizaba al perder el foco, así que al
  // abrir un usuario para editar salía vacío aunque el teléfono ya tuviera
  // operadora cargada.
  useEffect(() => {
    updatePhoneSearch()
  }, [updatePhoneSearch])

  const [profileSearch, setProfileSearch] = useState('')
  const [profileList, setProfileList] = useState<ProfileInterfase[]>([])
  const [departamentList, setDepartamentList] = useState<DepartmentInterfase[]>(
    [],
  )

  useEffect(() => {
    const accountUserID = formik.values.user.id
    if (accountUserID === undefined) return

    const fetchShipments = async () => {
      setLoadingStatus(ConfirmModalStatus.PROCESSING)
      setLoadingTitle('Cargando...')
      setLoading(true)
      const accountID = formik.values.user.accountID ?? '' + user?.client?.id
      // `'' + x ? ... : ...` siempre daba una cadena no vacía (incluso "NaN" o
      // "undefined"), así que el fallback al sitio del cliente nunca corría.
      const accountSiteID = Number.isNaN(
        Number(formik.values.user.accountSiteID),
      )
        ? user?.client?.accountSiteID?.toString()
        : formik.values.user.accountSiteID?.toString()

      const userAccountFind = await getAllUser(
        accountID,
        accountSiteID,
        accountUserID,
      )

      var lisUserAccount = (userAccountFind?.model ??
        []) as UserAccountsInterfase[]
      if (!userAccountFind?.didError && lisUserAccount.length > 0) {
        let client = lisUserAccount[0]
        setselectUserAccount(true)
        setDisabledIdentification(true)
        const resolvedProfileID = await resolveProfileForUser(
          client.userID,
          client.profileID,
        )

        formik.setValues({
          ...formik.values,
          user: {
            ...formik.values.user,
            userID: client.userID,
            userLogin: client.userLogin?.trim() ?? '',
            userName: client.userName?.trim() ?? '',
            userLastName: client.userLastName?.trim() ?? '',
            userIdentificationTypeID: identificationTypes.find(
              (t) =>
                t.taxIdentificationTypeId === client?.userIdentificationTypeID,
            )!,
            userIdentificationNumber:
              client.userIdentificationNumber?.trim() ?? '',
            profileID: resolvedProfileID,
            departamentID: undefined,
            statusID: client.statusID ?? undefined,
            codePhoneNumber: obtenerCodigoArea(client.userPhone?.trim()) ?? '',
            userPhone: obtenerNumero(client.userPhone?.trim()) ?? '',
            userEmail: client.userEmail?.trim() ?? '',
            accountSelected: client,
            reset: false,
          },
        })

        setLoadingStatus(ConfirmModalStatus.SUCCESS)
        setLoading(false)
      } else {
        setLoadingStatus(ConfirmModalStatus.ERROR)
        setLoadingTitle(
          userAccountFind?.didError
            ? userAccountFind.errorMessage || 'No se pudo cargar el usuario'
            : 'No se encontró el usuario en este cliente',
        )
        setLoading(true)
      }
    }
    fetchShipments()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!formik.values.user?.id, profileList, departamentList])

  const getLocations = async () => {
    const phonetype = await GeneralTypePhoneServices('236')
    if (!!phonetype.didError || !phonetype.model) return

    const emptyPhonePrefix = { value: 'FIJO', text: 'FIJO' }
    let phonetypeList = phonetype.model as unknown as GeneralTypeInterface[]
    phonetypeList.push(emptyPhonePrefix)

    setPhonePrefixList(
      (phonetypeList as unknown as GeneralTypeInterface[]).map(
        (phone) => phone.value,
      ),
    )
  }

  const getDepartament = async () => {
    const departament = await getAllDepartament()
    if (!!departament?.didError || !departament?.model) return
    setDepartamentList(departament.model)
  }

  const getProfile = async () => {
    const profile = await getAllProfile()
    if (!!profile?.didError || !profile?.model) return
    // "Super Administrador" queda en la lista: se muestra en el desplegable
    // pero bloqueado (ver `isProfileLocked`), y así también se puede resolver
    // su nombre al abrir un usuario que ya lo tiene.
    setProfileList(
      [...profile.model].sort((a, b) =>
        a.profileName.localeCompare(b.profileName),
      ),
    )
  }

  /** Un Super Administrador no puede cambiar de perfil desde esta pantalla. */
  const isSuperAdmin = formik.values.user.profileID === SUPER_ADMIN_PROFILE_ID

  // "Super Administrador" se lista pero no se puede elegir: esconderlo hacía
  // creer que el perfil no existía, y dejarlo seleccionable permite promover o
  // degradar un super admin desde esta pantalla sin ninguna traza.
  const isProfileLocked = useCallback(
    (profile: ProfileInterfase) => profile.profileID === SUPER_ADMIN_PROFILE_ID,
    [],
  )

  /**
   * El buscador de usuarios (`DynamicUserAccount`) devuelve el perfil que la
   * persona tenga en CUALQUIER aplicación (p. ej. 16 "Cliente Api"), no
   * necesariamente uno de MiTealca. Si no está en la lista de la aplicación 2
   * se descarta: antes quedaba el combo vacío pero con el ID inválido cargado y
   * se enviaba así al guardar.
   */
  const resolveProfile = useCallback(
    (profileID?: number) => {
      const match = profileList.find((p) => p.profileID === profileID)
      setProfileSearch(match?.profileName ?? '')
      return match?.profileID
    },
    [profileList],
  )

  /**
   * Perfil con el que hay que precargar el combo. El que trae el listado puede
   * venir de otra aplicación o no reflejar que la persona es Super
   * Administrador, así que se le pregunta al API por el perfil de MiTealca y
   * ESE manda cuando es 201: es el caso que hay que dejar bloqueado.
   */
  const resolveProfileForUser = useCallback(
    async (userID?: string, listedProfileID?: number) => {
      const real = await getUserProfileInApp(userID)
      return resolveProfile(
        pickProfileToPreload(real.profileID, listedProfileID),
      )
    },
    [resolveProfile],
  )

  const phoneRange = phoneDigitsFor(formik.values.user.codePhoneNumber)

  /**
   * El campo Usuario se abre para editar sólo si ya hay un login generado Y
   * algo falla con él. Cubre el caso que importa (dos "María Pérez" chocan y
   * hay que desempatar a mano) sin dejarlo editable en un alta en blanco, y sin
   * el callejón sin salida de antes, donde se comparaba contra un único mensaje
   * exacto y cualquier otro error dejaba el campo trabado y en rojo.
   */
  const loginNeedsFixing =
    !!errors?.userLogin && formik.values.user.userLogin.trim() !== ''

  useEffect(() => {
    getLocations()
    getDepartament()
    getProfile()
  }, [])

  useEffect(() => {
    const { userName, userLastName, userIdentificationTypeID, userID } =
      formik.values.user
    let userLogin = ''

    // El login sólo se autogenera para usuarios nuevos. Al abrir uno existente
    // este efecto se disparaba con los nombres recién cargados y le reescribía
    // el login real ("enderson.namias" -> "Enderson.Namias").
    if (!!userID) {
      return
    }

    if (`${userName}${userLastName}` === '') {
      return
    }

    userLogin = buildUserLogin(
      userName,
      userLastName,
      userIdentificationTypeID?.abbreviationName !== 'V-',
    )

    formik.setFieldValue('user.userLogin', userLogin)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.user.userID,
    formik.values.user.userName,
    formik.values.user.userLastName,
    formik.values.user.userIdentificationTypeID,
  ])

  return (
    <div className="flex h-full flex-col gap-1">
      {formik.values.user.id === undefined && (
        <div>
          <UsersSearchHistoric
            title=""
            selected={formik.values.user.accountSelected}
            error={
              touched?.accountSelected && errors?.accountSelected
                ? errors?.accountSelected
                : undefined
            }
            placeholder="Buscar usuario..."
            onSelectClient={async (client) => {
              setselectUserAccount(true)
              setDisabledIdentification(true)
              const resolvedProfileID = await resolveProfileForUser(
                client.userID,
                client.profileID,
              )
              formik.setValues({
                ...formik.values,
                user: {
                  ...formik.values.user,
                  userID: client.userID,
                  userLogin: client.userLogin?.trim() ?? '',
                  userName: client.userName?.trim() ?? '',
                  userLastName: client.userLastName?.trim() ?? '',
                  userIdentificationTypeID: identificationTypes.find(
                    (t) =>
                      t.taxIdentificationTypeId ===
                      client?.userIdentificationTypeID,
                  )!,
                  userIdentificationNumber:
                    client.userIdentificationNumber?.trim() ?? '',
                  profileID: resolvedProfileID,
                  departamentID:
                    client.departamentID !== 0
                      ? client.departamentID
                      : undefined,
                  statusID: client.statusID ?? undefined,
                  codePhoneNumber:
                    obtenerCodigoArea(client.userPhone?.trim()) ?? '',
                  userPhone: obtenerNumero(client.userPhone?.trim()) ?? '',
                  userEmail: client.userEmail?.trim() ?? '',
                  accountSelected: client,
                  reset: false,
                },
              })
            }}
            openCreationModal={() => {
              formik.setValues({
                ...formik.values,
                user: {
                  userID: undefined,
                  userLogin: '',
                  userName: '',
                  userLastName: '',
                  userIdentificationTypeID: identificationTypes.find(
                    (t) => t.abbreviationName === 'V-',
                  )!,
                  userIdentificationNumber: '',
                  profileID: undefined,
                  departamentID: undefined,
                  statusID: undefined,
                  userPhone: '',
                  codePhoneNumber: '',
                  userEmail: '',
                  accountSelected: {} as UserAccountsInterfase,
                  reset: false,
                },
              })
              setselectUserAccount(true)
              setDisabledIdentification(false)
            }}
          />{' '}
          <hr className="my-6" />
        </div>
      )}

      {selectUserAccount === true ||
      (formik.values.user.userID !== undefined &&
        formik.values.user.userID !== '') ? (
        <div className="flex h-full flex-col gap-1 mt-1 pl-6">
          <h4 className="text-sm text-main-500 gap-2 font-bold mb-3 mt-1">
            Detalles del usuario:
          </h4>
          <div className="flex flex-row">
            <FormText
              id="user.userLogin"
              name="user.userLogin"
              label="Usuario"
              value={formik.values.user.userLogin}
              error={
                touched?.userLogin && errors?.userLogin
                  ? errors?.userLogin
                  : undefined
              }
              // El login se autogenera y queda bloqueado: se abre SÓLO cuando
              // hay un error que corregir sobre un login ya generado (el caso
              // real es el choque de nombres: hay varias María Pérez y hay que
              // desempatar). Que el campo esté vacío no lo habilita, si no
              // arrancaría desbloqueado en cada alta nueva.
              disabled={!loginNeedsFixing}
              maxLength={USER_FIELD_LIMITS.userLogin.max}
              helperText={
                errors?.userLogin === LOGIN_TAKEN_MESSAGE
                  ? `Ese usuario ya está tomado. Pruebe agregando el segundo apellido o un número (máx. ${USER_FIELD_LIMITS.userLogin.max} caracteres).`
                  : undefined
              }
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="!h-8"
              labelClassname="!text-xs"
              labelContainerClassname="flex sm:!justify-end sm:w-32"
              containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
              style={{ maxWidth: '45rem' }}
            />
            <div
              role="status"
              aria-live="polite"
              className="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row ml-5"
            >
              {/* Los tres estados se comunicaban SOLO con color e ícono: un
                  lector de pantalla no anunciaba nada. `role="status"` +
                  `aria-live` y un texto sr-only por estado lo resuelven sin
                  cambiar lo que ve el operador. Los hex inline (#7a7a7a,
                  #166534) quedaban fuera de la paleta y del sistema env-aware;
                  ahora usan los tokens semánticos de DESIGN.md. */}
              {loadingUser === 0 && (
                <>
                  <ArrowPathIcon className="w-5 ml-2 animate-spin text-gray-400" />
                  <span className="sr-only">
                    Verificando disponibilidad del usuario
                  </span>
                </>
              )}

              {loadingUser === 1 && (
                <>
                  <CheckIcon className="w-5 ml-2 text-green-600" />
                  <span className="sr-only">Usuario disponible</span>
                </>
              )}

              {loadingUser === 2 && (
                <>
                  <XMarkIcon
                    className={`w-5 ml-2 ${
                      touched?.userLogin && errors?.userLogin ? '-mt-7' : ''
                    } text-sm text-red-600`}
                    title={LOGIN_TAKEN_MESSAGE}
                  />
                  <span className="sr-only">{LOGIN_TAKEN_MESSAGE}</span>
                </>
              )}
            </div>
          </div>

          <FormSelect
            autoComplete="off"
            id="user.userIdentificationTypeID"
            name="user.userIdentificationTypeID"
            label="Tipo de documento"
            selected={formik.values.user.userIdentificationTypeID}
            options={identificationTypes}
            error={
              touched?.userIdentificationTypeID &&
              errors?.userIdentificationTypeID
                ? errors?.userIdentificationTypeID
                : undefined
            }
            onBlur={formik.handleBlur}
            className="!h-7"
            labelClassname="!text-xs"
            labelContainerClassname="flex sm:!justify-end sm:w-32"
            containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
            style={{ maxWidth: '10rem' }}
            optionString={(option) => option.abbreviationName.slice(0, 1)}
            onSelectOption={(option) => {
              formik.setFieldValue('user.userIdentificationTypeID', option)
            }}
            disabled={disabledIdentification}
          />
          <FormText
            id="user.userIdentificationNumber"
            name="user.userIdentificationNumber"
            label="Nro. Documento"
            value={formik.values.user.userIdentificationNumber}
            error={
              touched?.userIdentificationNumber &&
              errors?.userIdentificationNumber
                ? errors?.userIdentificationNumber
                : undefined
            }
            onChange={(e) => {
              const max = USER_FIELD_LIMITS.userIdentificationNumber.max
              // Pasaporte admite letras; cédula/RIF sólo dígitos.
              const re =
                formik.values.user.userIdentificationTypeID
                  ?.abbreviationName !== 'P-'
                  ? new RegExp(`^[0-9]{0,${max}}$`)
                  : new RegExp(`^[a-zA-Z0-9]{0,${max}}$`)

              if (e.target.value === '' || re.test(e.target.value)) {
                formik.handleChange(e)
              }
            }}
            onBlur={formik.handleBlur}
            className="!h-8"
            labelClassname="!text-xs"
            labelContainerClassname="flex sm:!justify-end sm:w-32"
            containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
            style={{ maxWidth: '10rem' }}
            maxLength={USER_FIELD_LIMITS.userIdentificationNumber.max}
            helperText={`Máximo ${USER_FIELD_LIMITS.userIdentificationNumber.max} caracteres`}
            disabled={disabledIdentification}
          />
          <FormText
            id="user.userName"
            name="user.userName"
            label="Nombre"
            value={formik.values.user.userName}
            error={
              touched?.userName && errors?.userName
                ? errors?.userName
                : undefined
            }
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="!h-8"
            labelClassname="!text-xs"
            labelContainerClassname="flex sm:!justify-end sm:w-32"
            containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
            style={{ maxWidth: '45rem' }}
            maxLength={USER_FIELD_LIMITS.userName.max}
            helperText={`Máximo ${USER_FIELD_LIMITS.userName.max} caracteres`}
          />
          <FormText
            id="user.userLastName"
            name="user.userLastName"
            label="Apellido"
            value={formik.values.user.userLastName}
            error={
              touched?.userLastName && errors?.userLastName
                ? errors?.userLastName
                : undefined
            }
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="!h-8"
            labelClassname="!text-xs"
            labelContainerClassname="flex sm:!justify-end sm:w-32"
            containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
            style={{ maxWidth: '45rem' }}
            maxLength={USER_FIELD_LIMITS.userLastName.max}
            helperText={`Máximo ${USER_FIELD_LIMITS.userLastName.max} caracteres`}
          />

          <FormSearch
            id="user.codePhoneNumber"
            name="user.codePhoneNumber"
            label="Operadora"
            value={phonePreSearch}
            options={phonePrefixList}
            unselect={!!formik.values.user.codePhoneNumber}
            className="!h-8"
            labelClassname="!text-xs"
            labelContainerClassname="flex sm:!justify-end sm:w-32"
            containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
            error={
              touched?.codePhoneNumber && errors?.codePhoneNumber
                ? errors?.codePhoneNumber
                : undefined
            }
            onBlur={(e) => {
              // Usar un pequeño retraso para permitir la selección de la opción
              setTimeout(() => {
                if (
                  !e.relatedTarget ||
                  !e.relatedTarget.classList.contains('option-class')
                ) {
                  formik.handleBlur(e)
                }
              }, 200) // Ajusta el tiempo según sea necesario
            }}
            onChange={(e) => setPhonePreSearch(e.target.value)}
            onSelectOption={(option) => {
              formik.setFieldValue('user.codePhoneNumber', option)
              // Al pasar de FIJO (10-11 dígitos) a una operadora (7) el número
              // quedaba más largo de lo que el API acepta.
              const { max } = phoneDigitsFor(option)
              if (formik.values.user.userPhone.length > max) {
                formik.setFieldValue(
                  'user.userPhone',
                  formik.values.user.userPhone.slice(0, max),
                )
              }
            }}
            onChangeFocus={(focus) => !focus && updatePhoneSearch()}
            style={{ maxWidth: '45rem' }}
            onFilter={filterGeneral}
            RenderOption={({ option }) => <GenericoOption text={option} />}
          />

          <FormText
            id="user.userPhone"
            name="user.userPhone"
            label="Teléfono"
            value={formik.values.user.userPhone}
            error={
              touched?.userPhone && errors?.userPhone
                ? errors?.userPhone
                : undefined
            }
            onChange={(e) => {
              // El tope depende de la OPERADORA, no del número. Antes se
              // comparaba `userPhone !== 'FIJO'`, que nunca es falso, así que
              // el fijo se quedaba siempre en 7 dígitos.
              const re = new RegExp(`^[0-9]{0,${phoneRange.max}}$`)
              if (e.target.value === '' || re.test(e.target.value)) {
                formik.handleChange(e)
              }
            }}
            maxLength={phoneRange.max}
            helperText={
              formik.values.user.codePhoneNumber === 'FIJO'
                ? `${phoneRange.max} dígitos, con el código de área (ej. 2121234567)`
                : `${phoneRange.min} dígitos, sin la operadora`
            }
            onBlur={formik.handleBlur}
            className="!h-8"
            labelClassname="!text-xs"
            labelContainerClassname="flex sm:!justify-end sm:w-32"
            containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
            style={{ maxWidth: '45rem' }}
          />

          <FormText
            id="user.userEmail"
            name="user.userEmail"
            label="Correo"
            value={formik.values.user.userEmail}
            error={
              touched?.userEmail && errors?.userEmail
                ? errors?.userEmail
                : undefined
            }
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className="!h-8"
            labelClassname="!text-xs"
            labelContainerClassname="flex sm:!justify-end sm:w-32"
            containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
            style={{ maxWidth: '45rem' }}
            maxLength={USER_FIELD_LIMITS.userEmail.max}
            helperText={`Máximo ${USER_FIELD_LIMITS.userEmail.max} caracteres`}
          />

          {/* <FormSearch
            id="user.departamentID"
            name="user.departamentID"
            label="Departamento"
            value={departamentSearch}
            options={departamentList}
            unselect={!!formik.values.user.departamentID}
            className="!h-8"
            labelClassname="!text-xs"
            labelContainerClassname="flex sm:!justify-end sm:w-32"
            containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
            error={
              touched?.departamentID && errors?.departamentID
                ? errors?.departamentID
                : undefined
            }
            onBlur={formik.handleBlur}
            onChange={(e) => setDepartamentSearch(e.target.value)}
            onSelectOption={(option) => {
              setDepartamentSearch(option?.departamentName ?? '');
              formik.setFieldValue(`user.departamentID`, option?.departamentID);
            }}
            style={{ maxWidth: '45rem' }}
            onFilter={filterDepartament}
            RenderOption={({ option }) => <DepartmentOption {...option} />}
          /> */}

          <FormSearch
            id="user.profileID"
            name="user.profileID"
            label="Perfiles"
            className="!h-8"
            labelClassname="!text-xs"
            labelContainerClassname="flex sm:!justify-end sm:w-32"
            containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
            style={{ maxWidth: '45rem' }}
            value={profileSearch}
            options={profileList}
            optionDisabled={isProfileLocked}
            disabled={isSuperAdmin}
            unselect={!!formik.values.user.profileID && !isSuperAdmin}
            error={
              touched?.profileID && errors?.profileID
                ? errors?.profileID
                : undefined
            }
            onBlur={(e) => {
              // Usar un pequeño retraso para permitir la selección de la opción
              setTimeout(() => {
                if (
                  !e.relatedTarget ||
                  !e.relatedTarget.classList.contains('option-class')
                ) {
                  formik.handleBlur(e)
                }
              }, 200) // Ajusta el tiempo según sea necesario
            }}
            onChange={(e) => setProfileSearch(e.target.value)}
            onSelectOption={(option) => {
              setProfileSearch(option?.profileName ?? '')
              formik.setFieldValue(`user.profileID`, option?.profileID)
            }}
            onFilter={filterProfile}
            RenderOption={({ option }) => <ProfileOption {...option} />}
          />

          {isSuperAdmin && (
            <div className="flex flex-1 sm:ml-0 sm:gap-8 flex-col sm:flex-row">
              <div className="flex sm:!justify-end sm:w-32" />
              {/* amber-700 sobre blanco da 5.0:1; el amber-600 que tenía antes
                  se quedaba en 3.19:1, debajo del 4.5:1 de WCAG AA. Y text-tiny
                  (0.7rem = 11.2px) está por debajo del piso de 12px de la
                  escala de DESIGN.md. Es el único texto que explica por qué el
                  combo está deshabilitado: tiene que leerse. */}
              <div className="flex items-center gap-1 text-sm text-amber-700">
                <LockClosedIcon className="w-4 h-4 shrink-0 text-amber-600" />
                <span>
                  Perfil bloqueado: este usuario es Super Administrador y su
                  perfil no puede cambiarse desde esta pantalla.
                </span>
              </div>
            </div>
          )}

          <ModalLoading
            open={loading}
            title={loadingTitle}
            status={loadingStatus}
            setOpen={setLoading}
            onPrint={() => {}}
          />
        </div>
      ) : (
        <div className="red2" />
      )}
    </div>
  )
}
